import { useIntegrationAvailableTagsQuery } from '@Queries/index';
import * as Api from '@ViewModels';
import {
	MultipleSelectContent,
	MultipleSelectItem,
	MultipleSelectTrigger,
	MultipleSelectValue,
} from '@WebComponents/MultipleSelect';
import { SelectContent, SelectItem, SelectTrigger, SelectValue } from '@WebComponents/Select';
import React from 'react';
import { Button } from '../../../../../../../../web/components/Button';
import { SimpleTagSelector } from '../../../../../../../../web/components/entities/tags/SimpleTagSelector';
import { EditableMultipleSelect, EditableSelect, EditableToggle, useEditMode } from '../context';
import { styleSheet } from '../styles';
import { SettingGroup } from './SettingGroup';
import { SettingRow } from './SettingRow';

interface IMainSettingsGroupProps {
	settings: Api.IIntegrationSettings;
	impersonationContext: Api.IImpersonationContext;
	configuration: Api.IIntegrationConfiguration;
	provider: Api.IntegrationProvider;
	setSettings: (settings: Api.IIntegrationSettings) => void;
	originalIntegration: Api.IExternalIntegration;
}

export const MainSettingsGroup: React.FC<IMainSettingsGroupProps> = ({
	settings,
	impersonationContext,
	configuration,
	provider,
	setSettings,
	originalIntegration,
}) => {
	const [isTagSelectorModalOpen, setIsTagSelectorModalOpen] = React.useState(false);
	const availableTagsQuery = useIntegrationAvailableTagsQuery({
		impersonationContext,
		integrationProvider: provider,
	});

	const { isEditing } = useEditMode();

	const onUpdate = (updatedSettings: Partial<Api.IIntegrationSettings | Api.ISalesforceSettings>) => {
		setSettings({
			...settings,
			...updatedSettings,
		});
	};

	{
		/* We will revisit the concept of Prospect Integration.  Removing it from the UI for now. */
	}
	{
		/*
	const prospectOptionDisplay = React.useMemo(() => {
		if (!settings.prospectOptions?.prospectIntegration) return 'None selected';
		return settings.prospectOptions.prospectIntegration;
	}, [settings.prospectOptions?.prospectIntegration]);
	*/
	}

	const writebacksFieldDisplay = React.useMemo(() => {
		if (!settings.writeBacks?.length) return 'None selected';
		return settings.writeBacks.join(', ');
	}, [settings.writeBacks]);

	const segmentByTagsFieldDisplay = React.useMemo(() => {
		let displayValue = '';
		if (settings.segmentByTags?.enabled) {
			const { containsAll, exclude } = settings.segmentByTags;
			if (containsAll && exclude) {
				displayValue = 'Exclude contacts with all tags:';
			} else if (containsAll) {
				displayValue = 'Include contacts with all tags:';
			} else if (exclude) {
				displayValue = 'Exclude contacts with any of these tags:';
			}
		}
		if (settings.segmentByTags?.enabled && settings.segmentByTags?.tagValues?.length > 0) {
			displayValue += ` ${settings.segmentByTags.tagValues.join(', ')}`;
		}

		return displayValue;
	}, [settings.segmentByTags]);

	const showWritebacks = React.useMemo(() => {
		return provider !== Api.IntegrationProvider.ServiceTitan && provider !== Api.IntegrationProvider.QuickBooksOnline;
	}, [provider]);

	const isSalesforce = React.useMemo(() => {
		return provider === Api.IntegrationProvider.Salesforce;
	}, [provider]);

	const salesforceSettings = isSalesforce ? (settings as Api.ISalesforceSettings) : null;
	const salesforceConfiguration = isSalesforce ? (configuration as Api.ISalesforceIntegrationConfiguration) : null;
	const allAvailableSalesforceFields = {
		...salesforceConfiguration?.availableFields,
		...salesforceConfiguration?.availableCustomFields,
		...salesforceConfiguration?.availableDateFields,
		...salesforceConfiguration?.availableCustomDateFields,
	};
	const salesforceContactRecordType = salesforceSettings?.contactRecordType;
	const originalContactRecordType = (originalIntegration.settings as Api.ISalesforceSettings)?.contactRecordType;

	const getRemoteFieldDisplayName = (fieldId: string) => {
		return allAvailableSalesforceFields?.[fieldId] || fieldId;
	};

	const onConfirmTagBySegments = (containsAll: boolean, tagValues: string[], exclude: boolean) => {
		onUpdate({
			segmentByTags: {
				containsAll,
				enabled: true,
				exclude,
				tagValues,
			},
		});
		setIsTagSelectorModalOpen(false);
	};

	if (availableTagsQuery.isPending) return <div>Loading...</div>;
	const availableTags = availableTagsQuery?.data ?? [];
	const deceasedIndicatorOptions = [{ label: 'None', value: '__none__' }];
	Object.entries(allAvailableSalesforceFields ?? {}).forEach(([id, text]) => {
		deceasedIndicatorOptions.push({
			label: text,
			value: id,
		});
	});

	return (
		<>
			<SettingGroup title='Main Settings'>
				{isSalesforce ? (
					<SettingRow
						title='Contact Record Type'
						subtitle='Controls which Salesforce record type to use for contact syncing (cannot be changed once set)'
						error={salesforceContactRecordType === 'Unknown' ? '(No record type selected)' : null}
					>
						<EditableSelect
							value={salesforceContactRecordType}
							onValueChange={(value: string) => {
								onUpdate({ contactRecordType: value });
							}}
							displayValue={salesforceContactRecordType}
							disabled={originalContactRecordType !== 'Unknown'}
						>
							<SelectTrigger styles={styleSheet.editableField}>
								<SelectValue placeholder='Select record type' />
							</SelectTrigger>
							<SelectContent>
								{originalContactRecordType === 'Unknown' && (
									<SelectItem key='Unknown' value='Unknown'>
										Unknown
									</SelectItem>
								)}
								{salesforceConfiguration?.availableRecordTypes?.map(contactRecordType => (
									<SelectItem key={contactRecordType} value={contactRecordType}>
										{contactRecordType}
									</SelectItem>
								))}
							</SelectContent>
						</EditableSelect>
					</SettingRow>
				) : null}
				<SettingRow title='Mapping By' subtitle='Controls which role is used to establish ownership connections'>
					<EditableSelect
						value={settings.ownershipRole}
						onValueChange={(value: string) => onUpdate({ ownershipRole: value })}
						displayValue={settings.ownershipRole || 'Select ownership role'}
					>
						<SelectTrigger styles={styleSheet.editableField}>
							<SelectValue placeholder='Select ownership role' />
						</SelectTrigger>
						<SelectContent>
							{configuration.availableOwnershipRoles.map(role => (
								<SelectItem key={role} value={role}>
									{role}
								</SelectItem>
							))}
						</SelectContent>
					</EditableSelect>
				</SettingRow>
				{/* We will revisit the concept of Prospect Integration.  Removing it from the UI for now. */}
				{/*
				<SettingRow title='Prospect Integration' subtitle={prospectOptionDisplay}>
					<Select
						value={settings.prospectOptions?.prospectIntegration || Api.ProspectIntegration.None}
						onValueChange={(value: Api.ProspectIntegration) => {
							const timelineYears =
								value === Api.ProspectIntegration.All ? 0 : value === Api.ProspectIntegration.None ? 0 : 1; // TODO: Set this value if Api.ProspectIntegration.Timeline is selected
							onUpdate({
								prospectOptions: {
									prospectIntegration: value,
									timelineYears,
								},
							});
						}}
					>
						<SelectTrigger>
							<SelectValue placeholder='Select prospect integration' />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value={Api.ProspectIntegration.None}>None</SelectItem>
							<SelectItem value={Api.ProspectIntegration.All}>All</SelectItem>
							<SelectItem value={Api.ProspectIntegration.Timeline}>Timeline</SelectItem>
						</SelectContent>
					</Select>
				</SettingRow>
				 */}
				{/* We will revisit the concept of Primary Email only. Removing it from the UI as it does nothing in the backend at the moment */}
				{/* <SettingRow title='Primary Email Only' subtitle={settings.primaryEmailOnly ? 'Enabled' : 'Disabled'}>
					<EditableToggle
						id='primaryEmailOnly'
						isOn={settings.primaryEmailOnly}
						onToggleCheckChanged={checked => onUpdate({ primaryEmailOnly: checked })}
					/>
				</SettingRow> */}
				<SettingRow
					title='Segment By Tags'
					subtitle='Controls which contacts do (or do not) get synced based on assigned tags'
				>
					{settings.segmentByTags?.enabled && isEditing ? (
						<Button kind='primary' onClick={() => setIsTagSelectorModalOpen(true)}>
							Manage Tags
						</Button>
					) : null}
					<EditableToggle
						id='segmentByTags'
						isOn={settings.segmentByTags?.enabled}
						onToggleCheckChanged={checked => onUpdate({ segmentByTags: { enabled: checked } })}
						displayValue={segmentByTagsFieldDisplay || null}
					/>
				</SettingRow>
				{showWritebacks ? (
					<SettingRow title='Writebacks' subtitle='Select activities to be recorded back to the integration'>
						<EditableMultipleSelect
							value={settings.writeBacks || []}
							onChange={(values: string[]) => onUpdate({ writeBacks: values as Api.IntegrationWriteBackType[] })}
							displayValue={writebacksFieldDisplay}
						>
							<MultipleSelectTrigger styles={styleSheet.editableField}>
								<MultipleSelectValue values={settings.writeBacks || []} placeholder='Select writeback types' />
							</MultipleSelectTrigger>
							<MultipleSelectContent>
								{configuration.availableWriteBackTypes.map(type => {
									const isSelected = settings.writeBacks?.includes(type);
									return (
										<MultipleSelectItem key={type} value={type} selected={isSelected}>
											{type}
										</MultipleSelectItem>
									);
								})}
							</MultipleSelectContent>
						</EditableMultipleSelect>
					</SettingRow>
				) : null}
				{isSalesforce ? (
					<SettingRow title='Deceased Indicator' subtitle='Maps a Salesforce field to mark contacts as deceased'>
						<EditableSelect
							items={deceasedIndicatorOptions}
							value={salesforceSettings?.deceasedField || '__none__'}
							onValueChange={(value: string) => {
								onUpdate({ deceasedField: value === '__none__' ? '' : value });
							}}
							displayValue={getRemoteFieldDisplayName(salesforceSettings?.deceasedField) || 'None selected'}
						>
							<SelectTrigger styles={styleSheet.editableField}>
								<SelectValue placeholder='Select deceased indicator field' />
							</SelectTrigger>
							<SelectContent>
								{deceasedIndicatorOptions.map(o => (
									<SelectItem key={o.value} value={o.value}>
										{o.label}
									</SelectItem>
								))}
							</SelectContent>
						</EditableSelect>
					</SettingRow>
				) : null}
			</SettingGroup>

			{isTagSelectorModalOpen ? (
				<SimpleTagSelector
					isOpen
					onRequestClose={() => setIsTagSelectorModalOpen(false)}
					tags={availableTags}
					onConfirm={onConfirmTagBySegments}
					initialTags={settings.segmentByTags?.tagValues ?? []}
					initialContainsAll={settings.segmentByTags?.containsAll ?? false}
					initialExclude={settings.segmentByTags?.exclude ?? false}
				/>
			) : null}
		</>
	);
};
